import moment from "moment";
import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Badge, Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import { firestore } from "../../helpers/Firebase";
import { accessTypes, createNotification, getFirstCharacters } from "../../helpers/Utils";
import Order from "../../views/app/appointments/components/order";
import Customer from "../../views/app/CRM/components/Customer";
import { ORDER_COLLECTION } from "../../config/env";

const ChatHeading = ({ name, thumb, wa_id, selectedUser, onBackToChats, isMobile }) => {
  let chatMessages = selectedUser?.chatMessages?.sort((msg1, msg2) => msg1?.created - msg2?.created);
  const lastMessage = chatMessages?.[chatMessages?.length - 1];

  const { washers, services, advServices, } = useSelector(state => state.providers)
  const { user } = useSelector(state => state.authUser)

  const [activeServices, setActiveServices] = useState({})
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [loadingCustomer, setLoadingCustomer] = useState(false)
  const [labels, setLabels] = useState([])
  const [chatLabels, setChatLabels] = useState([])
  const [labelDropdownOpen, setLabelDropdownOpen] = useState(false)
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false)

  const dismiss = () => {
    setSelectedBooking(false)
  };

  const dismissCustomer = () => {
    setSelectedCustomer(null)
  };

  // Fetch all available labels
  useEffect(() => {
    if (!accessTypes.includes(user?.accessType) || !selectedUser?.id) return;

    const unsubscribe = firestore()
      .collection('chatLabels')
      .where('active', '==', true)
      .orderBy('created', 'desc')
      .onSnapshot(snap => {
        const labelsData = snap?.docs?.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        setLabels(labelsData);
      });

    return () => unsubscribe();
  }, [user, selectedUser]);

  // Fetch chat labels
  useEffect(() => {
    if (!selectedUser?.id) return;

    const chatDoc = firestore().collection('chats').doc(selectedUser.id);
    const unsubscribe = chatDoc.onSnapshot(snap => {
      if (snap.exists) {
        const data = snap.data();
        setChatLabels(data?.labels || []);
      }
    });

    return () => unsubscribe();
  }, [selectedUser]);

  const toggleLabelDropdown = () => setLabelDropdownOpen(prev => !prev);
  const toggleActionsDropdown = () => setActionsDropdownOpen(prev => !prev);

  const handleLabelToggle = async (labelId) => {
    if (!selectedUser?.id) return;

    try {
      const chatRef = firestore().collection('chats').doc(selectedUser.id);
      const chatDoc = await chatRef.get();
      const currentLabels = chatDoc.data()?.labels || [];

      let newLabels;
      if (currentLabels.includes(labelId)) {
        // Remove label
        newLabels = currentLabels.filter(id => id !== labelId);
      } else {
        // Add label
        newLabels = [...currentLabels, labelId];
      }

      await chatRef.update({
        labels: newLabels,
        updated: Date.now()
      });
    } catch (error) {
      console.error("Error updating chat labels:", error);
    }
  };

  const fetchCustomerData = async () => {
    if (!selectedUser?.uid) {
      return;
    }

    setLoadingCustomer(true);
    try {
      // Fetch user document
      const userDoc = await firestore().collection('users').doc(selectedUser.uid).get();

      if (!userDoc.exists) {
        setLoadingCustomer(false);
        return;
      }

      const userData = userDoc.data();
      const userNumber = Number(userData?.number || userData?.phoneNumber);

      // Fetch user orders
      let ordersRef = firestore().collection(ORDER_COLLECTION);
      if (!accessTypes.includes(user?.accessType)) {
        ordersRef = ordersRef.where("sp_id", "==", user?.uid);
      }

      const userBookingsSnap = await ordersRef.where('customer_number', '==', String(userNumber)).get();
      const userBookingsSnap2 = await ordersRef.where('customer_number', '==', String("0" + Number(userNumber))).get();

      const userBookings = [...userBookingsSnap.docs, ...userBookingsSnap2.docs].map(r => ({ ...r.data(), key: r.id })) || [];
      const createdArray = userBookings.map(r => r.created);
      const totalRevenue = userBookings?.map(r => (r?.totalPrice && r?.status !== 'canceled' && r?.status !== 'rejected' ? (r?.totalPrice * 1) : 0))?.reduce((a, b) => a + b, 0) || 0;

      const customerData = {
        ...userData,
        id: userDoc.id,
        key: userDoc.id,
        ordersPlaced: createdArray.length,
        firstOrder: createdArray.length ? Math.min.apply(null, createdArray) : new Date().getTime(),
        lastOrder: createdArray.length ? Math.max.apply(null, createdArray) : new Date().getTime(),
        revenue: totalRevenue,
        number: userData?.phoneNumber || userData?.number,
        orders: userBookings
      };

      setSelectedCustomer(customerData);
    } catch (error) {
      console.error("Error fetching customer data:", error);
    } finally {
      setLoadingCustomer(false);
    }
  };

  return (
    <Fragment>
      <div className="chat-header-container">
        <div className="chat-header-content">
          {isMobile && onBackToChats && (
            <button
              onClick={onBackToChats}
              className="chat-back-button"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '5px 10px',
                marginRight: '10px',
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Back to chats"
            >
              ←
            </button>
          )}
          <div className="chat-header-avatar">
            {getFirstCharacters(name || 'Chillapp Team')}
          </div>
          <div className="chat-header-info">
            <h5 className="chat-header-name" style={{ margin: 0, flex: 1, minWidth: 0 }}>{name}</h5>
            <p className="chat-header-time">{moment(lastMessage?.created).format('DD/MM/YYYY hh:mm A')}</p>
            {accessTypes.includes(user?.accessType) && chatLabels.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                {chatLabels.map(labelId => {
                  const label = labels.find(l => l.id === labelId);
                  if (!label) return null;
                  const labelColor = label.color || '#6c757d';
                  return (
                    <Badge
                      key={labelId}
                      className="chat-label-badge"
                      style={{
                        '--label-bg-color': labelColor,
                        backgroundColor: labelColor,
                        color: '#fff',
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        border: 'none'
                      }}
                    >
                      {label.name}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
          {isMobile && (
            <Dropdown isOpen={actionsDropdownOpen} toggle={toggleActionsDropdown}>
              <DropdownToggle caret color="primary" size="sm" outline className="chat-header-mobile-menu-btn">
                <i className="iconsminds-menu-line" style={{ fontSize: '16px', marginRight: '4px' }} />
                <span>Actions</span>
              </DropdownToggle>
              <DropdownMenu right style={{ minWidth: '200px', width: 'calc(100vw - 2rem)', maxWidth: '300px' }}>
                {accessTypes.includes(user?.accessType) && selectedUser?.id && (
                  <>
                    <DropdownItem header style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', color: '#6b7280' }}>
                      Labels
                    </DropdownItem>
                    {labels.length === 0 ? (
                      <DropdownItem disabled>No labels available</DropdownItem>
                    ) : (
                      labels.map(label => (
                        <DropdownItem
                          key={label.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleLabelToggle(label.id);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '0.75rem 1rem',
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            type="checkbox"
                            id={`mobile-label-${label.id}`}
                            checked={chatLabels.includes(label.id)}
                            onChange={() => {
                              handleLabelToggle(label.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              margin: 0,
                              flexShrink: 0,
                              cursor: 'pointer',
                              width: '18px',
                              height: '18px'
                            }}
                          />
                          <Badge
                            className="chat-label-badge"
                            style={{
                              '--label-bg-color': label.color || '#6c757d',
                              backgroundColor: label.color || '#6c757d',
                              color: '#fff',
                              fontSize: '11px',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              border: 'none',
                              fontWeight: 500
                            }}
                          >
                            {label.name}
                          </Badge>
                        </DropdownItem>
                      ))
                    )}
                    {(selectedUser?.uid || selectedUser?.orderNumber) && (
                      <DropdownItem divider style={{ margin: '0.5rem 0' }} />
                    )}
                  </>
                )}
                {selectedUser?.uid && (
                  <DropdownItem
                    onClick={() => {
                      fetchCustomerData();
                      setActionsDropdownOpen(false);
                    }}
                    disabled={loadingCustomer}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <i className="iconsminds-user" style={{ fontSize: '16px' }} />
                    <span>{loadingCustomer ? 'Loading...' : 'View Customer Profile'}</span>
                  </DropdownItem>
                )}
                {selectedUser?.orderNumber && (
                  <DropdownItem
                    onClick={async () => {
                      let allServices = Object.values(services || {})?.concat(Object.values(advServices || {}))
                      const updatedActiveServices = {};
                      Object.values(washers || {}).forEach(washer => {
                        if (washer.activeServices) {
                          const activeServiceKeys = Object.keys(washer.activeServices);
                          const filteredServices = allServices.filter(service => activeServiceKeys.includes(service.key));
                          filteredServices.forEach(service => {
                            updatedActiveServices[service.key] = {
                              ...service,
                              modifier: washer.activeServices[service.key],
                              label: service.name,
                              value: service.name
                            };
                          });
                          setActiveServices((prevActiveServices) => {
                            return { ...prevActiveServices, ...updatedActiveServices };
                          });
                        }
                      });
                      let userBookingsSnap = await firestore().collection(ORDER_COLLECTION).where('orderNumber', '==', selectedUser?.orderNumber).get()
                      if (userBookingsSnap.docs.length > 0) {
                        const booking = {
                          ...userBookingsSnap.docs[0]?.data() || {},
                          id: userBookingsSnap.docs[0]?.id,
                          key: userBookingsSnap.docs[0]?.id,
                        }
                        console.log(booking)
                        setSelectedBooking(booking)
                      } else {
                        createNotification('error', 'No order found for this customer', 'Order not found')
                        setSelectedBooking(null)
                      }
                      setActionsDropdownOpen(false);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <i className="iconsminds-shopping-cart" style={{ fontSize: '16px' }} />
                    <span>View Order Details</span>
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          )}
        </div>
        {!isMobile && (
          <div className="chat-header-actions">
            {accessTypes.includes(user?.accessType) && selectedUser?.id && (
              <Dropdown isOpen={labelDropdownOpen} toggle={toggleLabelDropdown}>
                <DropdownToggle caret color="secondary" size="sm" outline className="chat-header-action-btn">
                  <span className="chat-header-btn-text">Labels</span>
                </DropdownToggle>
                <DropdownMenu style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {labels.length === 0 ? (
                    <DropdownItem disabled>No labels available</DropdownItem>
                  ) : (
                    labels.map(label => (
                      <DropdownItem
                        key={label.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleLabelToggle(label.id);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '0.75rem 1rem',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="checkbox"
                          id={`desktop-label-${label.id}`}
                          checked={chatLabels.includes(label.id)}
                          onChange={() => {
                            handleLabelToggle(label.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            margin: 0,
                            flexShrink: 0,
                            cursor: 'pointer',
                            width: '18px',
                            height: '18px'
                          }}
                        />
                        <Badge
                          className="chat-label-badge"
                          style={{
                            '--label-bg-color': label.color || '#6c757d',
                            backgroundColor: label.color || '#6c757d',
                            color: '#fff',
                            fontSize: '11px',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            border: 'none',
                            fontWeight: 500
                          }}
                        >
                          {label.name}
                        </Badge>
                      </DropdownItem>
                    ))
                  )}
                </DropdownMenu>
              </Dropdown>
            )}
            {selectedUser?.uid && (
              <Button
                className="chat-header-button chat-header-action-btn"
                color="info"
                size="sm"
                outline
                disabled={loadingCustomer}
                onClick={fetchCustomerData}
              >
                <span className="chat-header-btn-text">
                  {loadingCustomer ? 'Loading...' : 'View Customer Profile'}
                </span>
              </Button>
            )}
            {selectedUser?.orderNumber && (
              <Button
                className="chat-header-button chat-header-action-btn"
                color="primary"
                size="sm"
                outline
                onClick={async () => {
                  let allServices = Object.values(services || {})?.concat(Object.values(advServices || {}))
                  const updatedActiveServices = {};
                  Object.values(washers || {}).forEach(washer => {
                    if (washer.activeServices) {
                      const activeServiceKeys = Object.keys(washer.activeServices);
                      const filteredServices = allServices.filter(service => activeServiceKeys.includes(service.key));
                      filteredServices.forEach(service => {
                        updatedActiveServices[service.key] = {
                          ...service,
                          modifier: washer.activeServices[service.key],
                          label: service.name,
                          value: service.name
                        };
                      });
                      setActiveServices((prevActiveServices) => {
                        return { ...prevActiveServices, ...updatedActiveServices };
                      });
                    }
                  });
                  let userBookingsSnap = await firestore().collection(ORDER_COLLECTION).where('orderNumber', '==', selectedUser?.orderNumber).get()
                  if (userBookingsSnap.docs.length > 0) {
                    const booking = {
                      ...userBookingsSnap.docs[0]?.data() || {},
                      id: userBookingsSnap.docs[0]?.id,
                      key: userBookingsSnap.docs[0]?.id,
                    }
                    console.log(booking)
                    setSelectedBooking(booking)
                  } else {
                    createNotification('error', 'No order found for this customer', 'Order not found')
                    setSelectedBooking(null)
                  }
                }}>
                <span className="chat-header-btn-text">
                  View Order Details
                </span>
              </Button>
            )}
          </div>
        )}
      </div>

      {selectedBooking && <Order
        activeServices={activeServices}
        toggleModal={dismiss}
        dismiss={dismiss}
        selectedBooking={JSON.parse(JSON.stringify(selectedBooking))}
      />
      }
      {selectedCustomer && <Customer
        customer={selectedCustomer}
        dismiss={dismissCustomer}
      />
      }
    </Fragment>
  );
};

export default ChatHeading;
